/**
 * ActivityToolbar — per-tab toolbar for the UserDrilldown Activity tab
 * (Plan 08-03 / ACT-04).
 *
 * Desktop (≥md): single horizontal row with rating <Select> + debounced
 * search <Input> + errors-only <Toggle> + active-filter clear pill.
 *
 * Mobile (<md): single `Filters (N)` button → bottom-sheet `<Dialog>` with
 * the same controls stacked vertically. Mirrors `FilterBar.tsx` /
 * `FilterModal.tsx` mobile pattern (sticky footer, `min-h-11` tap targets,
 * full-screen on phones).
 *
 * State ownership: this component is a CONTROLLED view — caller owns
 * `rating` / `search` / `errorsOnly` and the change callbacks. Wiring to
 * the URL is the caller's job (typically `useActivityUrlState`); the
 * toolbar itself is filter-state-agnostic.
 */

import { useEffect, useRef, useState } from 'react';
import { AlertCircle, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useViewport } from '@/hooks/use-viewport';
import { cn } from '@/lib/utils';

import type { ActivityRating } from '@/hooks/use-activity-url-state';

// ---------------------------------------------------------------------------
// Locked rating-select option list (CONTEXT C-3 §"Rating select")
// ---------------------------------------------------------------------------

interface RatingOption {
  value: ActivityRating;
  label: string;
}

const RATING_OPTIONS: ReadonlyArray<RatingOption> = [
  { value: 'any', label: 'Any rating' },
  { value: '5', label: '5★ only' },
  { value: '4+', label: '4★+' },
  { value: '3+', label: '3★+' },
  { value: '1-2', label: '1-2★' },
  { value: 'rated', label: 'Rated' },
  { value: 'unrated', label: 'Unrated' },
  { value: 'with_text', label: 'With text feedback' },
];

const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_MAX_LEN = 200;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ActivityToolbarProps {
  rating: ActivityRating;
  onRatingChange: (next: ActivityRating) => void;

  /** Currently committed search string (URL-mirrored). */
  search: string;
  /** Debounced — fires once 250ms after the last keystroke. */
  onSearchChange: (next: string) => void;

  errorsOnly: boolean;
  onErrorsOnlyChange: (next: boolean) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function countActive(props: ActivityToolbarProps): number {
  let n = 0;
  if (props.rating !== 'any') n += 1;
  if (props.search.length > 0) n += 1;
  if (props.errorsOnly) n += 1;
  return n;
}

/**
 * Local debounce: a controlled <Input> that surfaces keystrokes immediately
 * to the user but only fires the parent callback 250ms after the user
 * stops typing. We re-sync the local value when the parent's `value` prop
 * changes (URL-driven update — back/forward, deep link, programmatic
 * reset), but ignore prop changes that are echoes of our own debounced
 * write to avoid ping-ponging.
 */
function DebouncedSearchInput({
  value,
  onChange,
  testId,
}: {
  value: string;
  onChange: (next: string) => void;
  testId: string;
}): React.ReactElement {
  const [local, setLocal] = useState<string>(value);
  // Track the value we last surfaced UP via onChange so that incoming
  // prop changes that match it don't reset our local typing state.
  const lastEmittedRef = useRef<string>(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Re-sync local state when the parent's value diverges from what we
  // emitted (deep-link, reset button, etc.). If the parent is just
  // echoing back our last debounced emit, leave local alone.
  useEffect(() => {
    if (value !== lastEmittedRef.current) {
      lastEmittedRef.current = value;
      setLocal(value);
    }
  }, [value]);

  // Clear the pending timer on unmount so a late fire-after-unmount can't
  // call setState on a dead component.
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  function handleChange(next: string): void {
    setLocal(next);
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      lastEmittedRef.current = next;
      onChange(next);
    }, SEARCH_DEBOUNCE_MS);
  }

  return (
    <Input
      type="text"
      value={local}
      onChange={(e) => handleChange(e.target.value)}
      placeholder="Search prompt or response…"
      maxLength={SEARCH_MAX_LEN}
      className="w-64"
      data-testid={testId}
    />
  );
}

// ---------------------------------------------------------------------------
// Sub-components — locked option list rendered the same way on desktop +
// inside the mobile sheet so the test asserting "8 options" works in both
// variants.
// ---------------------------------------------------------------------------

function RatingSelect({
  value,
  onChange,
  testIdPrefix,
}: {
  value: ActivityRating;
  onChange: (next: ActivityRating) => void;
  testIdPrefix: string;
}): React.ReactElement {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as ActivityRating)}>
      <SelectTrigger
        className="h-8 w-44 text-xs"
        data-testid={`${testIdPrefix}-trigger`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {RATING_OPTIONS.map((o) => (
          <SelectItem
            key={o.value}
            value={o.value}
            data-testid={`${testIdPrefix}-option-${o.value}`}
          >
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function ErrorsToggle({
  pressed,
  onPressedChange,
  testId,
}: {
  pressed: boolean;
  onPressedChange: (next: boolean) => void;
  testId: string;
}): React.ReactElement {
  return (
    <button
      type="button"
      role="switch"
      aria-pressed={pressed}
      data-state={pressed ? 'on' : 'off'}
      onClick={() => onPressedChange(!pressed)}
      data-testid={testId}
      className={cn(
        'inline-flex h-8 items-center gap-1.5 rounded-md border border-input px-2.5 text-xs',
        'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
        pressed
          ? 'bg-primary text-primary-foreground border-primary'
          : 'bg-background hover:bg-accent hover:text-accent-foreground',
      )}
    >
      <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
      <span>Errors</span>
    </button>
  );
}

function ClearPill({
  count,
  onClear,
  testId,
}: {
  count: number;
  onClear: () => void;
  testId: string;
}): React.ReactElement | null {
  if (count <= 0) return null;
  return (
    <Badge
      variant="secondary"
      onClick={onClear}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClear();
        }
      }}
      className="cursor-pointer gap-1 bg-muted text-foreground hover:bg-muted/70"
      data-testid={testId}
    >
      <span>{count} active</span>
      <X className="h-3 w-3" aria-hidden="true" />
      <span>clear</span>
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ActivityToolbar(props: ActivityToolbarProps): React.ReactElement {
  const {
    rating,
    onRatingChange,
    search,
    onSearchChange,
    errorsOnly,
    onErrorsOnlyChange,
  } = props;
  const { isMobile } = useViewport();
  const [mobileOpen, setMobileOpen] = useState(false);

  const activeCount = countActive(props);

  function clearAll(): void {
    onRatingChange('any');
    onSearchChange('');
    onErrorsOnlyChange(false);
  }

  if (isMobile) {
    return (
      <div
        className="flex items-center gap-2 border-b bg-muted/30 px-3 py-2"
        data-testid="activity-toolbar"
        data-variant="mobile"
      >
        <Button
          variant="outline"
          size="sm"
          onClick={() => setMobileOpen(true)}
          className="min-h-11 flex-1 justify-start gap-2 text-xs"
          data-testid="activity-toolbar-mobile-trigger"
        >
          <span className="truncate">
            Filters{activeCount > 0 ? ` (${activeCount})` : ''}
          </span>
        </Button>

        <Dialog
          open={mobileOpen}
          onOpenChange={(next) => setMobileOpen(next)}
        >
          <DialogContent
            data-testid="activity-toolbar-mobile-sheet"
            className="!flex h-[100dvh] w-screen max-w-none flex-col gap-0 p-0 sm:h-auto sm:max-w-md sm:rounded-lg"
          >
            <DialogHeader className="shrink-0 border-b border-theme-border px-4 py-3">
              <DialogTitle className="text-left text-lg font-semibold">
                Activity filters
              </DialogTitle>
              <DialogDescription className="sr-only">
                Filter the Activity tab by rating, search text, or
                error-only rows.
              </DialogDescription>
            </DialogHeader>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5">
              <div className="flex flex-col gap-5">
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium">Rating</Label>
                  <RatingSelect
                    value={rating}
                    onChange={onRatingChange}
                    testIdPrefix="activity-rating"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label
                    htmlFor="activity-toolbar-mobile-search"
                    className="text-sm font-medium"
                  >
                    Search
                  </Label>
                  <Input
                    id="activity-toolbar-mobile-search"
                    type="text"
                    defaultValue={search}
                    placeholder="Search prompt or response…"
                    maxLength={SEARCH_MAX_LEN}
                    className="min-h-11"
                    onChange={(e) => onSearchChange(e.target.value)}
                    data-testid="activity-search-input-mobile"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">
                    Errors only
                  </Label>
                  <ErrorsToggle
                    pressed={errorsOnly}
                    onPressedChange={onErrorsOnlyChange}
                    testId="activity-errors-toggle-mobile"
                  />
                </div>
              </div>
            </div>

            <div
              className="shrink-0 flex flex-col gap-2 border-t border-theme-border bg-theme-background px-4 pt-3"
              style={{
                paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))',
              }}
            >
              <DialogClose asChild>
                <Button
                  size="lg"
                  className="min-h-11 w-full"
                  data-testid="activity-toolbar-mobile-apply"
                >
                  Done
                </Button>
              </DialogClose>
              <Button
                variant="ghost"
                size="lg"
                onClick={() => {
                  clearAll();
                  setMobileOpen(false);
                }}
                className="min-h-11 w-full text-theme-text-secondary"
                data-testid="activity-toolbar-mobile-reset"
              >
                Reset
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Desktop layout — single horizontal row.
  return (
    <div
      className="flex flex-wrap items-center gap-2 border-b bg-muted/30 px-3 py-2"
      data-testid="activity-toolbar"
      data-variant="desktop"
    >
      <RatingSelect
        value={rating}
        onChange={onRatingChange}
        testIdPrefix="activity-rating"
      />

      <DebouncedSearchInput
        value={search}
        onChange={onSearchChange}
        testId="activity-search-input"
      />

      <ErrorsToggle
        pressed={errorsOnly}
        onPressedChange={onErrorsOnlyChange}
        testId="activity-errors-toggle"
      />

      <ClearPill
        count={activeCount}
        onClear={clearAll}
        testId="activity-clear-pill"
      />
    </div>
  );
}
